import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useId, useState } from "react";
import "./wizard-preview.scss";

export interface PreviewSectionProps {
  /** Section heading text. */
  title: string;
  /** Optional leading icon (e.g. a lucide icon element). */
  icon?: React.ReactNode;
  /** Dims the section when its content is empty/inactive. */
  status?: "default" | "muted";
  /** When true, the header acts as a toggle that folds away the body. */
  collapsible?: boolean;
  /** Initial open state when collapsible. Defaults to open. */
  defaultOpen?: boolean;
  children: React.ReactNode;
}

/**
 * Generic titled block for the wizard preview column. Content-agnostic — pair
 * with PreviewKeyValueList / PreviewDslBlock or any consumer content. When
 * `collapsible`, the header becomes a toggle so the user can fold the section
 * away and focus on what matters at the current step.
 */
export const PreviewSection: React.FC<PreviewSectionProps> = ({
  title,
  icon,
  status = "default",
  collapsible = false,
  defaultOpen = true,
  children,
}) => {
  const bodyId = useId();
  const [open, setOpen] = useState(defaultOpen);

  const className = `tlx-preview-section${
    status === "muted" ? " tlx-preview-section--muted" : ""
  }`;

  if (!collapsible) {
    return (
      <section className={className}>
        <header className="tlx-preview-section__header">
          {icon && (
            <span className="tlx-preview-section__icon" aria-hidden>
              {icon}
            </span>
          )}
          <h5 className="tlx-preview-section__title">{title}</h5>
        </header>
        <div className="tlx-preview-section__body">{children}</div>
      </section>
    );
  }

  return (
    <section className={className}>
      <button
        type="button"
        className="tlx-preview-section__header tlx-preview-section__header--toggle"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen(prev => !prev)}
        data-test-id="preview-section-toggle"
      >
        <span className="tlx-preview-section__header-label">
          {icon && (
            <span className="tlx-preview-section__icon" aria-hidden>
              {icon}
            </span>
          )}
          <span className="tlx-preview-section__title">{title}</span>
        </span>
        <span className="tlx-preview-section__chevron" aria-hidden>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
      </button>
      {open && (
        <div className="tlx-preview-section__body" id={bodyId}>
          {children}
        </div>
      )}
    </section>
  );
};

export default PreviewSection;
